import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Shield, Clock3, Flame, Phone } from "lucide-react";
import { Button } from "../ui/button";
import { useTheme } from "../../context/ThemeContext";
import { isPresent, hasItems } from "./helpers";

const heroIconMap = { shield: Shield, clock: Clock3, flame: Flame };

export default function HeroSection({
  hero,
  heroFeatures,
  heroStats,
  heroBullets,
}) {
  const { theme } = useTheme();
  if (!hero) return null;

  return (
    <section
      data-testid="hero-section"
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden scroll-mt-24"
      style={{ top: "75px" }}
    >
      <div className="absolute inset-0 z-0">
        {isPresent(hero.background_image) &&
          (() => {
            const url = hero.background_image;
            const isVideo = /\.(mp4|webm|ogg)$/i.test(url);
            return (
              <div className="absolute inset-0 ken-burns">
                {isVideo ? (
                  <video
                    src={url}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={url}
                    alt="DVX Training"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            );
          })()}
        <div
          className={`absolute inset-0 transition-all duration-300 ${theme === "dark" ? "bg-black/60" : "bg-black/40"}`}
        />
      </div>

      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[var(--dvx-primary)]/20 blur-[150px] rounded-full z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-3">
        {isPresent(hero.badge) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-[var(--dvx-primary)]/10 border border-[var(--dvx-primary)]/30 text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest mb-6">
              {hero.badge}
            </span>
          </motion.div>
        )}

        {hasItems(heroFeatures) && (
          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center px-4 gap-4 my-3">
            {heroFeatures.map((item, i) => {
              if (!isPresent(item.text)) return null;
              const Icon = heroIconMap[item.icon] || Shield;

              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-3 py-2 backdrop-blur-md border border-gray-200 rounded-full shadow-sm hover:shadow-md transition w-full sm:w-auto ${
                    theme === "dark" ? "bg-black/40" : "bg-white/80"
                  }`}
                >
                  <span className="w-2 h-2 bg-orange-500 rounded-full shrink-0" />
                  <Icon className="h-4 w-4 shrink-0" />
                  <p
                    className={`text-sm font-medium ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {(isPresent(hero.title_prefix) || isPresent(hero.title_highlight)) && (
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-oswald text-5xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white uppercase leading-none tracking-tight"
          >
            {isPresent(hero.title_prefix) && hero.title_prefix}
            {isPresent(hero.title_prefix) &&
              isPresent(hero.title_highlight) && <br />}
            {isPresent(hero.title_highlight) && (
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)]">
                {hero.title_highlight}
              </span>
            )}
          </motion.h1>
        )}

        {isPresent(hero.description) && (
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`mt-6 px-4 text-lg md:text-xl font-manrope max-w-2xl mx-auto leading-relaxed text-justify sm:text-center ${
              theme === "dark" ? "text-[var(--dvx-text-muted)]" : "text-white"
            }`}
          >
            {hero.description}
          </motion.p>
        )}

        {(isPresent(hero.primary_cta?.label) ||
          isPresent(hero.secondary_cta?.label) ||
          isPresent(hero.tertiary_cta?.label)) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-10 px-4 flex flex-col sm:flex-row gap-4 justify-center items-stretch"
          >
            {isPresent(hero.primary_cta?.label) &&
              isPresent(hero.primary_cta?.href) && (
                <div className="w-full sm:w-auto sm:flex-none">
                  <Link to={hero.primary_cta.href} className="block w-full">
                    <Button className="w-full sm:w-auto rounded-none px-8 py-6 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold text-lg uppercase tracking-widest hover:scale-105 transition-transform">
                      {hero.primary_cta.label}
                      <ArrowRight className="ml-2 inline" size={20} />
                    </Button>
                  </Link>
                </div>
              )}

            {isPresent(hero.secondary_cta?.label) &&
              isPresent(hero.secondary_cta?.href) && (
                <div className="w-full sm:w-auto sm:flex-none">
                  <Link to={hero.secondary_cta.href} className="block w-full">
                    <Button
                      variant="outline"
                      className={`w-full sm:w-auto rounded-none px-8 py-6 font-oswald font-bold text-lg uppercase tracking-widest transition-all ${
                        theme === "dark"
                          ? "border-white/20 text-white hover:bg-white hover:text-black"
                          : "border-[var(--dvx-text)]/20 text-white hover:bg-[var(--dvx-text)] hover:text-white"
                      }`}
                    >
                      <Play className="mr-2 inline" size={20} />
                      {hero.secondary_cta.label}
                    </Button>
                  </Link>
                </div>
              )}

            {isPresent(hero.tertiary_cta?.label) &&
              isPresent(hero.tertiary_cta?.href) && (
                <div className="w-full sm:w-auto sm:flex-none">
                  <a href={hero.tertiary_cta.href} className="block w-full">
                    <Button className="w-full sm:w-auto rounded-none px-8 py-6 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold text-lg uppercase tracking-widest hover:scale-105 transition-transform">
                      {hero.tertiary_cta.label}
                      <Phone className="ml-2 inline" size={20} />
                    </Button>
                  </a>
                </div>
              )}
          </motion.div>
        )}

        {hasItems(heroStats) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 grid grid-cols-2 md:grid-cols-3 max-w-3xl mx-auto"
          >
            {heroStats.map((stat, i) => {
              if (!isPresent(stat.value) || !isPresent(stat.label)) return null;
              return (
                <div key={i} className="text-center">
                  <div
                    className={`font-oswald text-3xl md:text-4xl font-bold ${theme === "dark" ? "text-[var(--dvx-text)]" : "text-white"}`}
                  >
                    {stat.value}
                  </div>
                  <div
                    className={`text-sm uppercase tracking-wider mt-1 ${theme === "dark" ? "text-[var(--dvx-text-muted)]" : "text-white"}`}
                  >
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        {hasItems(heroBullets) && (
          <div className="flex justify-center mt-3">
            <div className="flex flex-wrap justify-start items-center gap-x-8 gap-y-3 rounded-full px-6 py-4 shadow-sm max-w-[90%] sm:max-w-fit">
              {heroBullets.map((text, i) => {
                if (!isPresent(text)) return null;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 whitespace-nowrap"
                  >
                    <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-orange-500 text-orange-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-3 h-3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <p
                      className={`text-sm font-medium ${
                        theme === "dark"
                          ? "text-[var(--dvx-text-muted)]"
                          : "text-white"
                      }`}
                    >
                      {text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
